import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, MapPin, Phone, Building2, User, ChevronLeft, ChevronRight, Zap, List, CalendarDays } from 'lucide-react';
import { toast } from 'react-hot-toast';
import CreneauxDisponibles from './CreneauxDisponibles';
import ProspectCalendar from './ProspectCalendar';

const API = process.env.REACT_APP_BACKEND_URL;

const PlanningPage = () => {
  const [rendezVous, setRendezVous] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTermPlanning, setSearchTermPlanning] = useState('');
  const [statusFilterPlanning, setStatusFilterPlanning] = useState('');
  const [sourceFilterPlanning, setSourceFilterPlanning] = useState('');
  const [prospectSources, setProspectSources] = useState([]);
  const [viewMode, setViewMode] = useState('week'); // 'day', 'week', 'month'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreneauxDialog, setShowCreneauxDialog] = useState(false);
  const [editingRdv, setEditingRdv] = useState(null);
  const [selectedProspectForCreneaux, setSelectedProspectForCreneaux] = useState(null);
  const [planningViewMode, setPlanningViewMode] = useState('agenda'); // 'agenda' ou 'list'
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // États pour les assignations (admin uniquement)
  const [commerciaux, setCommerciaux] = useState([]);
  const [formData, setFormData] = useState({
    prospect_id: '',
    date_rdv: '',
    duree_minutes: 60,
    type_rdv: 'visite_commerciale',
    status: 'planifie',
    resultat: '',
    commentaire_rdv: '',
    assigned_to: '' // Ajouter le champ d'assignation
  });

  useEffect(() => {
    getCurrentUser();
    fetchData();
  }, []);

  const getCurrentUser = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error getting current user:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Charger sources + users (nécessaires pour le dialog d'assignation en vue agenda)
      const [srcRes, usersRes] = await Promise.all([
        axios.get(`${API}/api/app-config/prospect_sources`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setProspectSources(srcRes.data?.sources || []);
      setCommerciaux((usersRes.data || []).filter(u => u.role === 'commercial' || u.role === 'regie'));
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Charger les données de la vue Liste seulement quand on y bascule
  const loadListData = async () => {
    if (rendezVous.length > 0 && prospects.length > 0) return; // déjà chargé
    try {
      const token = localStorage.getItem('token');
      const [prospectsRes, rdvRes, usersRes] = await Promise.all([
        axios.get(`${API}/api/prospects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/rendez-vous`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/users`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setProspects(prospectsRes.data);
      setCommerciaux((usersRes.data || []).filter(u => u.role === 'commercial' || u.role === 'regie'));
      const enrichedRdv = rdvRes.data.map(rdv => {
        const prospect = prospectsRes.data.find(p => p.id === rdv.prospect_id);
        return { ...rdv, prospect: prospect || null };
      });
      setRendezVous(enrichedRdv);
    } catch (e) { console.error(e); }
  };

  const openCreneauxDialog = () => {
    if (!formData.prospect_id) {
      toast.error('Veuillez sélectionner un prospect d\'abord');
      return;
    }
    
    const selectedProspect = prospects.find(p => p.id === formData.prospect_id);
    if (!selectedProspect) {
      toast.error('Prospect introuvable');
      return;
    }
    
    console.log('Selected prospect for creaneaux:', selectedProspect);
    console.log('Latitude:', selectedProspect.latitude, 'Longitude:', selectedProspect.longitude);
    
    if (!selectedProspect.latitude || !selectedProspect.longitude) {
      toast.error('Prospect non géolocalisé. Impossible de calculer les créneaux optimaux.');
      return;
    }
    
    setSelectedProspectForCreneaux(selectedProspect);
    setShowCreneauxDialog(true);
  };

  const handleCreateRdv = async () => {
    if (isSubmitting) return;
    try {
      if (!formData.prospect_id || !formData.date_rdv) {
        toast.error('Prospect et date sont obligatoires');
        return;
      }
      setIsSubmitting(true);

      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/rendez-vous`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Rendez-vous créé avec succès');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
      setCalendarRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error creating rendez-vous:', error);
      toast.error('Erreur lors de la création du rendez-vous');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateRdv = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/rendez-vous/${editingRdv.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Rendez-vous modifié avec succès');
      setShowEditDialog(false);
      setEditingRdv(null);
      resetForm();
      fetchData();
      setCalendarRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error('Error updating rendez-vous:', error);
      toast.error('Erreur lors de la modification');
    }
  };

  const resetForm = () => {
    setFormData({
      prospect_id: '',
      date_rdv: '',
      duree_minutes: 60,
      type_rdv: 'visite_commerciale',
      status: 'planifie',
      resultat: '',
      commentaire_rdv: '',
      assigned_to: '' // Ajouter le champ d'assignation
    });
  };

  // Fonctions pour les créneaux optimisés

  const handleSelectCreneau = (creneauData) => {
    setFormData({
      ...formData,
      date_rdv: creneauData.date_rdv.slice(0, 16), // Format pour input datetime-local
    });
    
    toast.success(`✨ Créneau optimal sélectionné: ${new Date(creneauData.date_rdv).toLocaleDateString('fr-FR')} à ${new Date(creneauData.date_rdv).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})} avec ${creneauData.suggested_commercial.first_name} ${creneauData.suggested_commercial.last_name}`);
  };

  // Fonction pour calculer la distance entre deux points
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return null;
    
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Fonction pour calculer les distances entre RDV consécutifs du même commercial
  const getDistanceToNextRdv = (currentRdv, allRdv) => {
    if (!currentRdv.prospect?.latitude || !currentRdv.assigned_to) return null;
    
    // Trouver tous les RDV du même commercial le même jour
    const sameDayRdv = allRdv
      .filter(rdv => 
        rdv.assigned_to === currentRdv.assigned_to &&
        new Date(rdv.date_rdv).toDateString() === new Date(currentRdv.date_rdv).toDateString() &&
        rdv.id !== currentRdv.id &&
        rdv.prospect?.latitude && rdv.prospect?.longitude
      )
      .sort((a, b) => new Date(a.date_rdv) - new Date(b.date_rdv));

    // Trouver le RDV suivant chronologiquement
    const currentTime = new Date(currentRdv.date_rdv);
    const nextRdv = sameDayRdv.find(rdv => new Date(rdv.date_rdv) > currentTime);
    
    if (!nextRdv) return null;

    const distance = calculateDistance(
      currentRdv.prospect.latitude, currentRdv.prospect.longitude,
      nextRdv.prospect.latitude, nextRdv.prospect.longitude
    );

    return {
      nextRdv,
      distance: distance?.toFixed(1),
      travelTime: Math.round((distance || 0) / 40 * 60) // 40km/h en ville
    };
  };

  const openEditDialog = (rdv) => {
    setEditingRdv(rdv);
    // Formater la date en local (pas UTC) pour le champ datetime-local
    const d = new Date(rdv.date_rdv);
    const localDate = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
    setFormData({
      prospect_id: rdv.prospect_id,
      date_rdv: localDate,
      duree_minutes: rdv.duree_minutes || 60,
      type_rdv: rdv.type_rdv || 'visite_commerciale',
      status: rdv.status || 'planifie',
      resultat: rdv.resultat || '',
      commentaire_rdv: rdv.commentaire_rdv || '',
      assigned_to: rdv.assigned_to || ''
    });
    setShowEditDialog(true);
  };

  // Navigation dans le calendrier
  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }
    setCurrentDate(newDate);
  };

  // Filtrer les RDV selon la vue actuelle et le rôle utilisateur
  const getFilteredRdv = () => {
    const startOfDay = new Date(currentDate);
    startOfDay.setHours(0, 0, 0, 0);
    
    let endDate = new Date(currentDate);
    let filteredByDate = [];
    
    if (viewMode === 'day') {
      endDate.setHours(23, 59, 59, 999);
      filteredByDate = rendezVous.filter(rdv => {
        const rdvDate = new Date(rdv.date_rdv);
        return rdvDate >= startOfDay && rdvDate <= endDate;
      });
    } else if (viewMode === 'week') {
      const dayOfWeek = currentDate.getDay();
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - dayOfWeek);
      startOfWeek.setHours(0, 0, 0, 0);
      
      endDate = new Date(startOfWeek);
      endDate.setDate(startOfWeek.getDate() + 6);
      endDate.setHours(23, 59, 59, 999);
      
      filteredByDate = rendezVous.filter(rdv => {
        const rdvDate = new Date(rdv.date_rdv);
        return rdvDate >= startOfWeek && rdvDate <= endDate;
      });
    } else if (viewMode === 'month') {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59, 999);
      
      filteredByDate = rendezVous.filter(rdv => {
        const rdvDate = new Date(rdv.date_rdv);
        return rdvDate >= startOfMonth && rdvDate <= endOfMonth;
      });
    } else {
      filteredByDate = rendezVous.filter(rdv => {
        const rdvDate = new Date(rdv.date_rdv);
        return rdvDate >= startOfDay && rdvDate <= endDate;
      });
    }

    // Filtrer selon le rôle de l'utilisateur
    if (!currentUser) return filteredByDate;

    if (currentUser.role === 'admin' || currentUser.role === 'administratif') {
      // Admin et Administratif voient tous les RDV
      return filteredByDate;
    } else if (currentUser.role === 'commercial' || currentUser.role === 'regie') {
      // Commercial et Régie ne voient que leurs RDV attribués
      return filteredByDate.filter(rdv => rdv.assigned_to === currentUser.id);
    } else if (currentUser.role === 'telepro') {
      // Telepro voit ses RDV créés ET attribués à lui
      return filteredByDate.filter(rdv => 
        rdv.created_by === currentUser.id || rdv.assigned_to === currentUser.id
      );
    }

    return filteredByDate;
  };

  // Appliquer filtres recherche/statut/source sur la vue liste
  const getFilteredRdvList = () => {
    let filtered = getFilteredRdv();
    if (searchTermPlanning) {
      const s = searchTermPlanning.toLowerCase();
      filtered = filtered.filter(rdv => {
        const p = prospects.find(p => p.id === rdv.prospect_id);
        return p && (
          (p.nom || '').toLowerCase().includes(s) || (p.prenom || '').toLowerCase().includes(s) ||
          (p.telephone || '').includes(s) || (p.adresse || '').toLowerCase().includes(s) ||
          (p.code_postal || '').includes(s) || (p.ville || '').toLowerCase().includes(s)
        );
      });
    }
    if (statusFilterPlanning) {
      filtered = filtered.filter(rdv => rdv.status === statusFilterPlanning || rdv.resultat === statusFilterPlanning);
    }
    if (sourceFilterPlanning) {
      filtered = filtered.filter(rdv => {
        const p = prospects.find(p => p.id === rdv.prospect_id);
        return p && p.source_prospect === sourceFilterPlanning;
      });
    }
    return filtered;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planifie': return 'bg-blue-100 text-blue-800';
      case 'realise': return 'bg-green-100 text-green-800';
      case 'reporte': return 'bg-yellow-100 text-yellow-800';
      case 'annule': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'planifie': return 'Planifié';
      case 'realise': return 'Réalisé';
      case 'reporte': return 'Reporté';
      case 'annule': return 'Annulé';
      default: return status;
    }
  };

  const getResultatColor = (resultat) => {
    switch (resultat) {
      case 'signe': return 'bg-green-100 text-green-800';
      case 'non_signe': return 'bg-red-100 text-red-800';
      case 'r2': return 'bg-orange-100 text-orange-800';
      case 'hors_critere': return 'bg-gray-100 text-gray-800';
      case 'pas_interesse': return 'bg-orange-100 text-orange-800';
      default: return '';
    }
  };

  const getResultatLabel = (resultat) => {
    switch (resultat) {
      case 'signe': return 'Signé';
      case 'non_signe': return 'Non signé';
      case 'r2': return 'R2';
      case 'hors_critere': return 'Hors critère';
      case 'pas_interesse': return 'Pas intéressé';
      default: return '';
    }
  };

  const formatDateHeader = () => {
    const options = { 
      year: 'numeric', 
      month: 'long', 
      day: viewMode === 'day' ? 'numeric' : undefined 
    };
    
    if (viewMode === 'week') {
      const dayOfWeek = currentDate.getDay();
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - dayOfWeek);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      return `Semaine du ${startOfWeek.toLocaleDateString('fr-FR')} au ${endOfWeek.toLocaleDateString('fr-FR')}`;
    }
    
    return currentDate.toLocaleDateString('fr-FR', options);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Chargement du planning...</div>
      </div>
    );
  }

  const filteredRdv = getFilteredRdv();

  // === Composant Gestion Plannings — Grille jour par jour ===
  const SECTORS = [
    { key: 'rhone_alpes', label: 'Rhône-Alpes', departements: ['38','01','42','73','71','74','39','25','21','63','03','43','07','26'], color: 'blue' },
    { key: 'idf_alentours', label: 'IDF & Alentours', departements: ['80','02','60','27','28','95','91','94','92','77','59','62','10','76','45','78'], color: 'purple' },
  ];

  const PlanningSlots = () => {
    const [slots, setSlots] = React.useState({});
    const [defaults, setDefaults] = React.useState({});
    
    React.useEffect(() => { fetchSlots(); fetchDefaults(); }, []);
    
    const fetchDefaults = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/app-config/planning_defaults`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }));
        setDefaults(res.data || {});
      } catch (e) { setDefaults({}); }
    };
    
    const updateDefault = async (sectorKey, value) => {
      const newDefaults = { ...defaults, [sectorKey]: Math.max(0, value) };
      setDefaults(newDefaults);
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API}/api/app-config/planning_defaults`, newDefaults, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) { console.error(e); }
    };
    
    const getSlotValue = (dateStr, sectorKey) => {
      const key = `${dateStr}_${sectorKey}`;
      if (slots[key]) return slots[key].max_rdv;
      return defaults[sectorKey] || 0;
    };
    
    const isCustomized = (dateStr, sectorKey) => {
      return !!slots[`${dateStr}_${sectorKey}`];
    };
    
    const fetchSlots = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/api/planning-slots`, { headers: { Authorization: `Bearer ${token}` } });
        const map = {};
        (res.data || []).forEach(s => { map[`${s.date}_${s.secteur}`] = s; });
        setSlots(map);
      } catch (e) { console.error(e); }
    };
    
    const updateSlot = async (date, sector, delta) => {
      const current = getSlotValue(date, sector.key);
      const newVal = Math.max(0, current + delta);
      try {
        const token = localStorage.getItem('token');
        const key = `${date}_${sector.key}`;
        if (newVal === 0 && slots[key]?.id) {
          await axios.delete(`${API}/api/planning-slots/${slots[key].id}`, { headers: { Authorization: `Bearer ${token}` } });
        } else if (newVal !== (defaults[sector.key] || 0) || slots[key]) {
          // Sauvegarder seulement si différent du défaut
          await axios.post(`${API}/api/planning-slots`, {
            id: slots[key]?.id, date, secteur: sector.key, departements: sector.departements, max_rdv: newVal
          }, { headers: { Authorization: `Bearer ${token}` } });
        }
        fetchSlots();
      } catch (e) { console.error(e); }
    };

    // Générer les 14 prochains jours ouvrés
    const days = [];
    let d = new Date(); d.setDate(d.getDate() + 1);
    while (days.length < 14) {
      const dow = d.getDay();
      if (dow !== 0 && dow !== 6) days.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    
    return (
      <Card className="mt-3">
        <CardContent className="p-3">
          <h3 className="text-sm font-bold text-gray-700 mb-2">📅 Plannings disponibles</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-1.5 w-24">Jour</th>
                  {SECTORS.map(s => (
                    <th key={s.key} className="text-center p-1.5">
                      <span className={`text-${s.color}-600 font-bold`}>{s.label}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Ligne valeur par défaut */}
                <tr className="bg-amber-50 border-b-2 border-amber-200">
                  <td className="p-1.5 font-bold text-amber-700">⚙️ Par défaut</td>
                  {SECTORS.map(sector => {
                    const val = defaults[sector.key] || 0;
                    return (
                      <td key={sector.key} className="text-center p-1">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => updateDefault(sector.key, val - 1)}
                            className="w-6 h-6 rounded-full bg-amber-100 hover:bg-red-100 text-amber-700 hover:text-red-600 flex items-center justify-center font-bold text-sm">−</button>
                          <span className="w-8 text-center text-sm font-bold text-amber-700">{val}</span>
                          <button onClick={() => updateDefault(sector.key, val + 1)}
                            className="w-6 h-6 rounded-full bg-amber-100 hover:bg-green-100 text-amber-700 hover:text-green-600 flex items-center justify-center font-bold text-sm">+</button>
                        </div>
                      </td>
                    );
                  })}
                </tr>
                {days.map(day => {
                  const dateStr = day.toISOString().split('T')[0];
                  const dayLabel = day.toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: '2-digit' });
                  return (
                    <tr key={dateStr} className="border-b hover:bg-gray-50">
                      <td className="p-1.5 font-medium capitalize">{dayLabel}</td>
                      {SECTORS.map(sector => {
                        const count = getSlotValue(dateStr, sector.key);
                        const custom = isCustomized(dateStr, sector.key);
                        return (
                          <td key={sector.key} className="text-center p-1">
                            <div className="flex items-center justify-center gap-1">
                              <button onClick={() => updateSlot(dateStr, sector, -1)}
                                className="w-6 h-6 rounded-full bg-gray-100 hover:bg-red-100 text-gray-600 hover:text-red-600 flex items-center justify-center font-bold text-sm">−</button>
                              <span className={`w-8 text-center text-sm font-bold ${custom ? 'text-orange-600 underline' : count > 0 ? `text-${sector.color}-600` : 'text-gray-300'}`}>
                                {count}
                              </span>
                              <button onClick={() => updateSlot(dateStr, sector, 1)}
                                className="w-6 h-6 rounded-full bg-gray-100 hover:bg-green-100 text-gray-600 hover:text-green-600 flex items-center justify-center font-bold text-sm">+</button>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-[10px] text-gray-400 mt-1">Les valeurs en <span className="text-orange-600 underline">orange souligné</span> sont personnalisées. Les autres suivent le défaut.</p>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6">
      {/* Header responsive */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-xl md:text-2xl font-bold">Planning</h1>
        
        <div className="flex items-center gap-3">
          {/* Toggle Agenda/Liste */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setPlanningViewMode('agenda')}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors
                ${planningViewMode === 'agenda' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <CalendarDays className="w-4 h-4" /> Agenda
            </button>
            <button
              onClick={() => { setPlanningViewMode('list'); loadListData(); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors
                ${planningViewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <List className="w-4 h-4" /> Liste
            </button>
          </div>
          {(currentUser?.role === 'telepro' || currentUser?.role === 'admin' || currentUser?.role === 'confirmateur' || currentUser?.role === 'superviseur') && (
            <Button 
              onClick={() => setShowCreateDialog(true)} 
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Nouveau RDV
            </Button>
          )}
        </div>
      </div>

      {/* VUE AGENDA */}
      {planningViewMode === 'agenda' ? (
        <>
        <div style={{ height: 'calc(100vh - 160px)' }}>
          <ProspectCalendar
            onSlotClick={(dateTime) => {
              setShowCreateDialog(true);
            }}
            onEditRdv={(rdv) => {
              openEditDialog(rdv);
            }}
            onDeleteRdv={async (rdv) => {
              try {
                const token = localStorage.getItem('token');
                await axios.delete(`${API}/api/rendez-vous/${rdv.id}`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Rendez-vous supprimé');
                fetchData();
                setCalendarRefreshKey(prev => prev + 1);
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Erreur suppression');
              }
            }}
            onRefreshNeeded={calendarRefreshKey}
          />
        </div>

        {/* Section Plannings — visible pour admin/confirmateur/administratif */}
        {(currentUser?.role === 'admin' || currentUser?.role === 'confirmateur' || currentUser?.role === 'administratif') && (
          <PlanningSlots />
        )}
        </>
      ) : (
      <>
      {/* VUE LISTE — Contenu existant */}

      {/* Barre de recherche et filtres */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Input placeholder="Rechercher (nom, téléphone, ville...)"
                value={searchTermPlanning} onChange={e => setSearchTermPlanning(e.target.value)} />
            </div>
            <div>
              <select className="w-full h-9 px-3 border rounded-md text-sm" value={statusFilterPlanning}
                onChange={e => setStatusFilterPlanning(e.target.value)}>
                <option value="">Tous les statuts</option>
                <option value="planifie">Planifié</option>
                <option value="realise">Réalisé</option>
                <option value="reporte">Reporté</option>
                <option value="annule">Annulé</option>
                <option value="signe">Signé</option>
                <option value="r2">R2</option>
              </select>
            </div>
            <div>
              <select className="w-full h-9 px-3 border rounded-md text-sm" value={sourceFilterPlanning}
                onChange={e => setSourceFilterPlanning(e.target.value)}>
                <option value="">Toutes les sources</option>
                {prospectSources.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contrôles de navigation responsive */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Sélecteur de vue */}
            <div className="flex gap-2 order-2 sm:order-1">
              <Button 
                variant={viewMode === 'day' ? 'default' : 'outline'}
                onClick={() => setViewMode('day')}
                size="sm"
              >
                Jour
              </Button>
              <Button 
                variant={viewMode === 'week' ? 'default' : 'outline'}
                onClick={() => setViewMode('week')}
                size="sm"
              >
                Semaine
              </Button>
              <Button 
                variant={viewMode === 'month' ? 'default' : 'outline'}
                onClick={() => setViewMode('month')}
                size="sm"
              >
                Mois
              </Button>
            </div>

            {/* Navigation de date */}
            <div className="flex items-center gap-4 order-1 sm:order-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigateDate('prev')}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="text-center min-w-[200px]">
                <div className="font-semibold text-sm md:text-base">
                  {formatDateHeader()}
                </div>
              </div>
              
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigateDate('next')}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Bouton aujourd'hui */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setCurrentDate(new Date())}
              className="order-3"
            >
              Aujourd'hui
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Statistiques du jour/période */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="text-lg md:text-2xl font-bold">{filteredRdv.length}</div>
            <div className="text-xs md:text-sm text-gray-600">RDV total</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="text-lg md:text-2xl font-bold text-blue-600">
              {filteredRdv.filter(r => r.status === 'planifie').length}
            </div>
            <div className="text-xs md:text-sm text-gray-600">Planifiés</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="text-lg md:text-2xl font-bold text-green-600">
              {filteredRdv.filter(r => r.resultat === 'signe').length}
            </div>
            <div className="text-xs md:text-sm text-gray-600">Signés</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 md:p-4">
            <div className="text-lg md:text-2xl font-bold text-red-600">
              {filteredRdv.filter(r => r.resultat === 'non_signe').length}
            </div>
            <div className="text-xs md:text-sm text-gray-600">Non signés</div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des RDV - Vue responsive */}
      <div className="space-y-4">
        {filteredRdv.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <div>Aucun rendez-vous pour cette période</div>
            </CardContent>
          </Card>
        ) : (
          filteredRdv.map((rdv) => (
            <Card key={rdv.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Informations principales */}
                  <div className="flex-1 space-y-2">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-sm md:text-base">
                          {new Date(rdv.date_rdv).toLocaleDateString('fr-FR')}
                        </span>
                        <span className="text-sm text-gray-600">
                          {new Date(rdv.date_rdv).toLocaleTimeString('fr-FR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </span>
                      </div>
                      
                      <div className="flex gap-2">
                        <Badge className={getStatusColor(rdv.status)}>
                          {getStatusLabel(rdv.status)}
                        </Badge>
                        {rdv.resultat && (
                          <Badge className={getResultatColor(rdv.resultat)}>
                            {getResultatLabel(rdv.resultat)}
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Infos prospect */}
                    <div className="space-y-1 text-sm">
                      {rdv.prospect && (
                        <>
                          <div className="flex items-center gap-2">
                            <User className="w-3 h-3 text-gray-400" />
                            <span>
                              {rdv.prospect.prenom} {rdv.prospect.nom}
                              {rdv.prospect.nom_entreprise && (
                                <span className="text-gray-600"> - {rdv.prospect.nom_entreprise}</span>
                              )}
                            </span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Phone className="w-3 h-3 text-gray-400" />
                            <span>{rdv.prospect.telephone}</span>
                          </div>
                          
                          <div className="flex items-start gap-2">
                            <MapPin className="w-3 h-3 text-gray-400 mt-0.5" />
                            <span className="break-words">
                              {rdv.prospect.adresse}
                              {(rdv.prospect.code_postal || rdv.prospect.ville) && (
                                <span className="text-gray-500">
                                  {rdv.prospect.code_postal && ` - ${rdv.prospect.code_postal}`}
                                  {rdv.prospect.ville && ` ${rdv.prospect.ville}`}
                                </span>
                              )}
                            </span>
                          </div>
                        </>
                      )}
                    </div>

                    {/* Distance vers le prochain RDV */}
                    {(() => {
                      const nextRdvInfo = getDistanceToNextRdv(rdv, filteredRdv);
                      return nextRdvInfo && (
                        <div className="text-xs text-orange-600 bg-orange-50 p-2 rounded flex items-center gap-1">
                          <span className="font-semibold">→</span>
                          <span>
                            Prochain RDV: {nextRdvInfo.nextRdv.prospect.prenom} {nextRdvInfo.nextRdv.prospect.nom} 
                            ({nextRdvInfo.distance}km, ~{nextRdvInfo.travelTime}min)
                          </span>
                        </div>
                      );
                    })()}

                    {rdv.commentaire_rdv && (
                      <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                        <strong>Commentaire:</strong> {rdv.commentaire_rdv}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openEditDialog(rdv)}
                    >
                      Modifier
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      </>
      )}

      {/* Dialog Création RDV (pour Telepro) */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Nouveau Rendez-vous</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Prospect *</Label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={formData.prospect_id}
                onChange={(e) => setFormData({...formData, prospect_id: e.target.value})}
              >
                <option value="">Sélectionner un prospect...</option>
                {prospects.map(prospect => (
                  <option key={prospect.id} value={prospect.id}>
                    {prospect.prenom} {prospect.nom}
                    {prospect.nom_entreprise && ` - ${prospect.nom_entreprise}`}
                  </option>
                ))}
              </select>
            </div>

            {/* Créneaux Optimisés IA */}
            {formData.prospect_id && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      Intelligence Artificielle - Créneaux Optimisés
                    </h3>
                    <p className="text-sm text-blue-600 mt-1">
                      L'IA analyse tous les RDV existants pour proposer les meilleurs créneaux
                    </p>
                  </div>
                  <Button 
                    onClick={openCreneauxDialog}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    type="button"
                    disabled={!formData.prospect_id}
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Créneaux IA
                  </Button>
                </div>
              </div>
            )}

            <div>
              <Label>Date et heure *</Label>
              <Input
                type="datetime-local"
                value={formData.date_rdv}
                onChange={(e) => setFormData({...formData, date_rdv: e.target.value})}
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>

            <div>
              <Label>Durée (minutes)</Label>
              <Input
                type="number"
                value={formData.duree_minutes}
                onChange={(e) => setFormData({...formData, duree_minutes: parseInt(e.target.value)})}
              />
            </div>

            <div>
              <Label>Type de RDV</Label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={formData.type_rdv}
                onChange={(e) => setFormData({...formData, type_rdv: e.target.value})}
              >
                <option value="visite_commerciale">Visite commerciale</option>
                <option value="appel_telephonique">Appel téléphonique</option>
                <option value="reunion">Réunion</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="flex-1">
              Annuler
            </Button>
            <Button onClick={handleCreateRdv} className="bg-blue-600 hover:bg-blue-700 flex-1">
              Créer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Modification RDV */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le Rendez-vous</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Date et heure *</Label>
              <Input
                type="datetime-local"
                value={formData.date_rdv}
                onChange={(e) => setFormData({...formData, date_rdv: e.target.value})}
              />
            </div>

            <div>
              <Label>Statut</Label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                value={formData.status}
                onChange={(e) => setFormData({...formData, status: e.target.value})}
              >
                <option value="planifie">Planifié</option>
                <option value="realise">Réalisé</option>
                <option value="reporte">Reporté</option>
                <option value="annule">Annulé</option>
              </select>
            </div>

            {/* Assignation Commercial (Admin et Confirmateur) */}
            {(currentUser?.role === 'admin' || currentUser?.role === 'confirmateur') && (
              <div>
                <Label>Assigner à (Commercial/Régie)</Label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.assigned_to || ''}
                  onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}
                >
                  <option value="">Non assigné</option>
                  {commerciaux.map(commercial => (
                    <option key={commercial.id} value={commercial.id}>
                      {commercial.first_name} {commercial.last_name} ({commercial.role})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {formData.status === 'realise' && (
              <div>
                <Label>Résultat</Label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  value={formData.resultat}
                  onChange={(e) => setFormData({...formData, resultat: e.target.value})}
                >
                  <option value="">Sélectionner...</option>
                  <option value="signe">Signé ✅</option>
                  <option value="r2">R2 — 2ème rendez-vous</option>
                  <option value="r2_devis">R2 — Demande de devis</option>
                  <option value="r2_reflexion">R2 — En réflexion</option>
                  <option value="non_signe">Non signé</option>
                  <option value="hors_critere">Hors critère</option>
                  <option value="pas_interesse">Pas intéressé</option>
                </select>
              </div>
            )}

            <div>
              <Label>Commentaire</Label>
              <Textarea
                value={formData.commentaire_rdv}
                onChange={(e) => setFormData({...formData, commentaire_rdv: e.target.value})}
                rows={3}
                placeholder="Ajouter un commentaire sur le rendez-vous..."
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">
              Annuler
            </Button>
            <Button onClick={handleUpdateRdv} className="bg-blue-600 hover:bg-blue-700 flex-1">
              Sauvegarder
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Créneaux Disponibles */}
      {selectedProspectForCreneaux && (
        <CreneauxDisponibles
          isOpen={showCreneauxDialog}
          onClose={() => {
            setShowCreneauxDialog(false);
            setSelectedProspectForCreneaux(null);
          }}
          prospectAddress={selectedProspectForCreneaux.adresse}
          prospectLatitude={selectedProspectForCreneaux.latitude}
          prospectLongitude={selectedProspectForCreneaux.longitude}
          onSelectCreneau={handleSelectCreneau}
        />
      )}
    </div>
  );
};

export default PlanningPage;